import React from 'react';
import { Box, Button } from '@material-ui/core';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Alert, CircularProgress, FormHelperText, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { ModalTitle, ModalTypography } from 'components/Modal/Modal';
import { ModalStep } from 'components/Flow/components/ModalStep';
import { DeedHeartHands } from 'components/Flow/components/DeedHeartHands';
import { StepComponentProps } from 'components/Flow/RenderMachine';
import { FilesUpload } from 'containers/FilesUpload/FilesUpload';

export const ProofOfAssociationUpload = ({
  isTransitionAllowed,
  send,
  state,
}: StepComponentProps): JSX.Element => {
  const { handleSubmit, control } = useForm({
    defaultValues: {
      supportingDocuments: [],
      role: '',
    },
  });

  const proofUploadState =
    (typeof state.value === 'object' &&
      'nces' in state.value &&
      typeof state.value.nces === 'object' &&
      'proofOfAssociationUpload' in state.value.nces &&
      state.value.nces.proofOfAssociationUpload) ||
    (typeof state.value === 'object' &&
      'non501c3Gov' in state.value &&
      typeof state.value.non501c3Gov === 'object' &&
      'proofOfAssociationUpload' in state.value.non501c3Gov &&
      state.value.non501c3Gov.proofOfAssociationUpload);

  const isSubmitting = proofUploadState === 'submitting';
  const isFailed = proofUploadState === 'failure';

  const onSubmit = (data: any) => {
    send({
      type: 'REQUEST_VERIFICATION',
      supportingDocuments: data.supportingDocuments,
      role: data.role,
    });
  };

  return (
    <ModalStep
      showHelpCenterLink={false}
      showProgressBar
      actions={
        <div>
          {isTransitionAllowed('SKIP') && (
            <Button
              color="inherit"
              type="button"
              endIcon={<ArrowForwardIcon style={{ fontSize: 16 }} />}
              style={{ marginRight: 8 }}
              onClick={(): unknown => send('SKIP')}
            >
              Skip
            </Button>
          )}
          <Button
            type="button"
            variant="contained"
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {isSubmitting ? <CircularProgress size={21} /> : isFailed ? 'Try again' : 'Confirm'}
          </Button>
        </div>
      }
      header={<DeedHeartHands />}
      headerStyles={{
        paddingTop: 16,
      }}
    >
      <Box>
        <ModalTitle>User Verification</ModalTitle>
        <ModalTypography>
          To confirm that users are authorized to act on behalf of your organization, please upload
          one of the following:
        </ModalTypography>
        <ul style={{ paddingLeft: 12 }}>
          <li>
            <ModalTypography>A cancelled check from your organization</ModalTypography>
          </li>
          <li>
            <ModalTypography>
              A bank letter confirming your organization’s account details
            </ModalTypography>
          </li>
        </ul>
        <ModalTypography>
          These documents help us manually verify that the user is connected to the organization
          they’re claiming.
        </ModalTypography>
        <Controller
          control={control}
          name="supportingDocuments"
          rules={{
            validate: (value) => value?.length > 0 || 'Please upload at least one document.',
          }}
          render={({ field: { onChange }, fieldState }) => (
            <>
              <FilesUpload
                s3Acl="private"
                s3Bucket="secure"
                onChange={(files, uploadInProgress) => {
                  if (!uploadInProgress) {
                    onChange(files.map((file) => file.publicUrl).filter(Boolean));
                  }
                }}
                multiple
              />
              {fieldState.error ? (
                <FormHelperText error={Boolean(fieldState.error)} style={{ marginTop: 8 }}>
                  {fieldState.error.message}
                </FormHelperText>
              ) : null}
            </>
          )}
        />

        <Controller
          control={control}
          name="role"
          rules={{
            required: 'The role is required',
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                label="Role"
                helperText="What is your title/role at this company/organization?"
                fullWidth
                required
                variant="standard"
                error={Boolean(fieldState.error)}
                style={{ marginTop: 24 }}
              />
              {fieldState.error ? (
                <FormHelperText error={Boolean(fieldState.error)} style={{ marginTop: -8 }}>
                  {fieldState.error.message}
                </FormHelperText>
              ) : null}
            </>
          )}
        />

        {isFailed && (
          <Box mt={2}>
            <Alert severity="error">Failed to submit the file, please try again</Alert>
          </Box>
        )}
      </Box>
    </ModalStep>
  );
};
